/**
 * Parallax effect for newsletter section images
 * Moves images at different speeds based on scroll position
 */

class NewsletterParallax {
  constructor(section) {
    this.section = section;
    this.images = Array.from(section.querySelectorAll(".image-block"));
    this.isActive = false;
    this.fixedButton = document.getElementById("fixed-button");

    // Parallax speeds for each image (negative values move up, positive move down)
    // Different speeds create depth effect
    this.speeds = [
      -1.0, // First image - slow
      -2, // Second image - faster, opposite direction
      -1.2, // Third image
    ];

    // Store references for cleanup
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.scrollTimeout = null;
    this.pendingRAF = null;
    this.fixedButtonObserver = null;

    this.handleScroll = this.handleScroll.bind(this);
    this.init();
    this.initFixedButtonObserver();
  }

  initFixedButtonObserver() {
    if (!this.fixedButton) return;

    const fixedButton = this.fixedButton;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!fixedButton) return;
          if (entry.isIntersecting) {
            // Newsletter section is visible, hide fixed button
            fixedButton.classList.add("fixed-button-hidden");
          } else {
            // Newsletter section is not visible, show fixed button
            fixedButton.classList.remove("fixed-button-hidden");
          }
        });
      },
      {
        threshold: 0.2, // Trigger when 20% of section is visible
        rootMargin: "0px",
      }
    );

    observer.observe(this.section);
    this.fixedButtonObserver = observer;
  }

  init() {
    if (this.images.length === 0) return;

    // Check if user prefers reduced motion
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    this.isActive = true;
    this.updateParallax();

    // Use requestAnimationFrame for smooth performance
    let ticking = false;
    const rafHandler = () => {
      if (!ticking) {
        this.pendingRAF = window.requestAnimationFrame(() => {
          this.updateParallax();
          ticking = false;
          this.pendingRAF = null;
        });
        ticking = true;
      }
    };

    // Throttle scroll events
    this.scrollHandler = () => {
      if (this.scrollTimeout) {
        window.cancelAnimationFrame(this.scrollTimeout);
        this.scrollTimeout = null;
      }
      this.scrollTimeout = window.requestAnimationFrame(rafHandler);
    };

    window.addEventListener("scroll", this.scrollHandler, { passive: true });

    // Also handle resize to recalculate positions
    this.resizeHandler = () => {
      this.updateParallax();
    };
    window.addEventListener("resize", this.resizeHandler, { passive: true });
  }

  updateParallax() {
    if (!this.isActive) return;

    const sectionRect = this.section.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Get section's position relative to viewport
    const sectionTop = sectionRect.top;
    const sectionHeight = sectionRect.height;
    const sectionBottom = sectionRect.bottom;

    // Calculate progress as section scrolls through viewport
    // Progress: 0 when section bottom enters viewport, 1 when section top exits viewport
    // This creates smooth parallax as the section scrolls normally (no sticky behavior)
    let progress = 0;

    if (sectionBottom > 0 && sectionTop < windowHeight) {
      // Section is visible in viewport
      // Calculate progress: 0 at entry (bottom hits viewport bottom), 1 at exit (top hits viewport top)
      const totalScrollDistance = sectionHeight + windowHeight;
      const currentScrollDistance = windowHeight - sectionTop;
      progress = currentScrollDistance / totalScrollDistance;
      progress = Math.max(0, Math.min(1, progress)); // Clamp between 0 and 1
    } else if (sectionTop >= windowHeight) {
      // Section hasn't entered viewport yet
      progress = 0;
    } else if (sectionBottom <= 0) {
      // Section has completely exited viewport
      progress = 1;
    }

    // Apply parallax transform to each image
    this.images.forEach((image, index) => {
      const speed = this.speeds[index] || 0;
      // Calculate offset based on progress and speed
      // Negative speeds move up, positive move down
      const maxOffset = 150; // Maximum parallax offset in pixels
      const offset = progress * maxOffset * speed;

      // Apply transform (preserve translateZ for hardware acceleration)
      image.style.transform = `translateY(${offset}px) translateZ(0)`;
      // Optimize for performance
      image.style.willChange = "transform";
    });
  }

  handleScroll() {
    this.updateParallax();
  }

  destroy() {
    this.isActive = false;

    // Clean up images
    this.images.forEach((image) => {
      image.style.transform = "";
      image.style.willChange = "";
    });

    // Cancel pending requestAnimationFrame calls
    if (this.scrollTimeout) {
      window.cancelAnimationFrame(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    if (this.pendingRAF) {
      window.cancelAnimationFrame(this.pendingRAF);
      this.pendingRAF = null;
    }

    // Remove event listeners
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }

    // Disconnect intersection observer
    if (this.fixedButtonObserver) {
      this.fixedButtonObserver.disconnect();
      this.fixedButtonObserver = null;
    }
  }
}

// Track instances for cleanup
const parallaxInstances = new WeakMap();

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initParallax);
} else {
  initParallax();
}

function initParallax() {
  const newsletterSections = document.querySelectorAll(".newsletter-section");
  newsletterSections.forEach((section) => {
    // Skip if already initialized
    if (parallaxInstances.has(section)) return;
    const instance = new NewsletterParallax(section);
    parallaxInstances.set(section, instance);
  });
}

// Re-initialize when sections are dynamically loaded (e.g., via section rendering)
document.addEventListener("shopify:section:load", (event) => {
  const section = event.detail.section.querySelector(".newsletter-section");
  if (section) {
    // Clean up existing instance if any
    const existingInstance = parallaxInstances.get(section);
    if (existingInstance) {
      existingInstance.destroy();
    }
    const instance = new NewsletterParallax(section);
    parallaxInstances.set(section, instance);
  }
});

// Clean up when sections are unloaded
document.addEventListener("shopify:section:unload", (event) => {
  const section = event.detail.section.querySelector(".newsletter-section");
  if (section) {
    const instance = parallaxInstances.get(section);
    if (instance) {
      instance.destroy();
      parallaxInstances.delete(section);
    }
  }
});
